
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, Sparkles, Zap, Leaf, Activity, Utensils, Brain, BookMarked } from 'lucide-react';

export const PlanComparison: React.FC = () => {
    return (
        <div className="bg-sage-50 min-h-screen pb-16">
            {/* Hero Section */}
            <div className="bg-sage-900 text-white py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">Choose Your Wellness Journey</h1>
                    <p className="text-xl md:text-2xl text-sage-100 max-w-2xl mx-auto font-light">
                        Start free or unlock the full power of personalized natural healing
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">

                {/* Comparison Title */}
                <div className="text-center mb-12">
                    <div className="inline-block bg-white px-8 py-3 rounded-full shadow-lg text-sage-900 font-bold text-xl border border-sage-200">
                        Free Plan vs Healer Plan <Sparkles className="inline-block text-yellow-500 w-5 h-5 ml-2" />
                    </div>
                </div>

                {/* Comparison Table */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-sage-200 mb-16">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-sage-50 border-b border-sage-100">
                                    <th className="p-6 text-left text-sage-900 font-bold text-lg w-1/3">Feature</th>
                                    <th className="p-6 text-center text-sage-900 font-bold text-lg w-1/3">Free Plan</th>
                                    <th className="p-6 text-center text-sage-900 font-bold text-lg w-1/3 bg-sage-100/50">Healer Plan <Sparkles className="inline-block w-4 h-4 text-yellow-500" /></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-sage-100">
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Wellness Assessment</td>
                                    <td className="p-6 text-center text-sage-600">
                                        <div className="flex flex-col items-center gap-1">
                                            <div className="flex items-center justify-center gap-2">
                                                <Check className="text-green-600 w-5 h-5" />
                                                <span>Prakriti · Core Nature</span>
                                            </div>
                                            <span className="text-xs text-sage-500">Vikriti · Current Imbalance</span>
                                        </div>
                                    </td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex flex-col items-center gap-1">
                                            <div className="flex items-center justify-center gap-2">
                                                <Check className="text-green-600 w-5 h-5" />
                                                <span>Prakriti · Core Nature</span>
                                            </div>
                                            <span className="text-xs text-sage-500 font-normal">Vikriti · Current Imbalance</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">AI Consultations</td>
                                    <td className="p-6 text-center text-sage-600">3 per day</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Unlimited
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Wellness Profile</td>
                                    <td className="p-6 text-center text-sage-600">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Full access
                                        </div>
                                    </td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Full access
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Voice Input</td>
                                    <td className="p-6 text-center text-sage-600">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Basic voice queries
                                        </div>
                                    </td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Full voice consultation
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Botanical Rx</td>
                                    <td className="p-6 text-center text-sage-600">Herb recommendations only</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Complete prescriptions
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Yoga Aid</td>
                                    <td className="p-6 text-center text-gray-400">—</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Personalized practices
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Nutri Heal</td>
                                    <td className="p-6 text-center text-gray-400">—</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Personalised food guidance & healing recipes
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Saved Library</td>
                                    <td className="p-6 text-center text-gray-400">—</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Save & organize
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-6 text-sage-800 font-medium">Priority Support</td>
                                    <td className="p-6 text-center text-gray-400">—</td>
                                    <td className="p-6 text-center text-sage-900 font-bold bg-sage-50/30">
                                        <div className="flex items-center justify-center gap-2">
                                            <Check className="text-green-600 w-5 h-5" /> Faster response
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Detailed Plan Breakdown */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                    {/* Free Plan */}
                    <div className="bg-white rounded-3xl p-8 border-2 border-sage-100 flex flex-col h-full">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-sage-900 mb-2">Free Plan: Explore Natural Wellness</h2>
                            <p className="text-sage-600">Perfect for getting started with personalized natural health guidance</p>
                        </div>
                        <div className="flex-1 space-y-6">
                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Brain size={20} className="text-sage-500" /> 3 Daily Consultations</h3>
                                <p className="text-sage-700 text-sm">Ask health questions and receive guidance grounded in 200+ ancient texts and 37,000+ verified knowledge points</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Activity size={20} className="text-sage-500" /> Wellness Profile (Wellness Passport)</h3>
                                <p className="text-sage-700 text-sm">Complete your comprehensive health assessment to personalize all recommendations to your unique body constitution</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Zap size={20} className="text-sage-500" /> Voice Input</h3>
                                <p className="text-sage-700 text-sm">Type or speak your health queries for convenient consultation access</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Leaf size={20} className="text-sage-500" /> Basic Botanical Guidance</h3>
                                <p className="text-sage-700 text-sm">Learn which herbs and natural remedies can support your health (without detailed dosages)</p>
                            </div>
                        </div>
                        <div className="mt-8 pt-8 border-t border-sage-100 text-center">
                            <div className="text-3xl font-bold text-sage-900 mb-4">FREE</div>
                            <Link to="/app" className="block w-full py-3 px-6 bg-sage-100 text-sage-800 font-bold rounded-xl hover:bg-sage-200 transition-colors">
                                Get Started Free
                            </Link>
                        </div>
                    </div>

                    {/* Healer Plan */}
                    <div className="bg-sage-50 rounded-3xl p-8 border-2 border-sage-500 relative flex flex-col h-full shadow-lg">
                        <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl flex items-center gap-1">
                            <Sparkles size={12} /> MOST POPULAR
                        </div>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-sage-900 mb-2 flex items-center gap-2">Healer Plan <Sparkles className="text-yellow-500" /></h2>
                            <p className="text-sage-600">Unlock Complete Personalized Healing for those serious about natural wellness</p>
                        </div>
                        <div className="flex-1 space-y-6">
                            <div className="bg-white/50 p-3 rounded-lg -mx-3 border border-sage-200/50">
                                <p className="font-bold text-sage-900 text-sm flex items-center gap-2"><Check size={16} className="text-green-600" /> Everything in Free, PLUS:</p>
                            </div>

                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Brain size={20} className="text-sage-600" /> Unlimited AI Consultations</h3>
                                <p className="text-sage-700 text-sm">Ask as many health questions as you need, whenever you need—no daily limits</p>
                            </div>

                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Leaf size={20} className="text-sage-600" /> Complete Botanical Rx</h3>
                                <ul className="text-sage-700 text-sm list-disc pl-5 space-y-1">
                                    <li>Precise dosages tailored to your body type</li>
                                    <li>Exact timing, duration, and preparation instructions</li>
                                    <li>Safety considerations and benefits explained</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Activity size={20} className="text-sage-600" /> Full Yoga Aid Access</h3>
                                <ul className="text-sage-700 text-sm list-disc pl-5 space-y-1">
                                    <li>Targeted asanas for your specific imbalances</li>
                                    <li>Breathwork (Pranayama) techniques</li>
                                    <li>Restorative routines & modifications</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><Utensils size={20} className="text-sage-600" /> Targeted Nutri Heal Plans</h3>
                                <ul className="text-sage-700 text-sm list-disc pl-5 space-y-1">
                                    <li>Foods to embrace/avoid & healing spices</li>
                                    <li>Therapeutic meal plans & herbal infusion recipes</li>
                                    <li>Six Tastes balancing guidance</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-sage-900 flex items-center gap-2 mb-2"><BookMarked size={20} className="text-sage-600" /> Saved Library</h3>
                                <p className="text-sage-700 text-sm">Save all your prescriptions and build your wellness archive</p>
                            </div>
                        </div>
                        <div className="mt-8 pt-8 border-t border-sage-200 text-center">
                            <div className="text-3xl font-bold text-sage-900 mb-1">$14.99<span className="text-base font-normal text-sage-600">/month</span></div>
                            <div className="space-y-3 mt-6">
                                <Link to="/app" className="block w-full py-3 px-6 bg-sage-600 text-white font-bold rounded-xl hover:bg-sage-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                    Start 14-Day Free Trial
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Why Upgrade Section */}
                <div className="bg-white rounded-3xl p-8 md:p-12 mb-16 shadow-lg border border-sage-100">
                    <h2 className="text-3xl font-bold text-sage-900 mb-8 text-center">Why Upgrade to Healer Plan?</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div>
                            <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-4">
                                <Target size={24} />
                            </div>
                            <h3 className="font-bold text-lg text-sage-900 mb-2">Complete Personalization</h3>
                            <p className="text-sage-700">Free gives you guidance. Healer gives you precise prescriptions with dosages, timing, and step-by-step instructions.</p>
                        </div>
                        <div>
                            <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-4">
                                <Leaf size={24} />
                            </div>
                            <h3 className="font-bold text-lg text-sage-900 mb-2">Multi-Dimensional Healing</h3>
                            <p className="text-sage-700">Address health from all angles—herbs, movement, and nutrition working together for your unique constitution.</p>
                        </div>
                        <div>
                            <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-4">
                                <BookMarked size={24} />
                            </div>
                            <h3 className="font-bold text-lg text-sage-900 mb-2">Build Your Wellness Library</h3>
                            <p className="text-sage-700">Save and organize everything that works for you. Create your personal reference guide that grows with your journey.</p>
                        </div>
                        <div>
                            <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-4">
                                <Zap size={24} />
                            </div>
                            <h3 className="font-bold text-lg text-sage-900 mb-2">No Limits</h3>
                            <p className="text-sage-700">Unlimited consultations mean you can explore every health question, refine recommendations, and get support whenever you need it.</p>
                        </div>
                        <div>
                            <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-4">
                                <Brain size={24} />
                            </div>
                            <h3 className="font-bold text-lg text-sage-900 mb-2">Deeper Learning</h3>
                            <p className="text-sage-700">With access to all modules, you'll truly learn to read your body's signals and become empowered in your wellness journey.</p>
                        </div>
                    </div>
                </div>

                {/* Testimonials */}
                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    <div className="bg-sage-50 p-6 rounded-2xl italic text-sage-700 border border-sage-100">
                        "The detailed dosages in Botanical Rx gave me the confidence to actually use the herbs. It's not just 'try turmeric'—it's exactly how much, when, and why."
                        <div className="mt-4 font-bold not-italic text-sage-900">— Priya S., Healer Plan member</div>
                    </div>
                    <div className="bg-sage-50 p-6 rounded-2xl italic text-sage-700 border border-sage-100">
                        "Having Yoga Aid, Nutri Heal, and Botanical Rx work together transformed my approach to managing my digestion issues. Everything is personalized to MY body."
                        <div className="mt-4 font-bold not-italic text-sage-900">— Rahul M., Healer Plan member</div>
                    </div>
                    <div className="bg-sage-50 p-6 rounded-2xl italic text-sage-700 border border-sage-100">
                        "The Saved Library is a game-changer. I have my go-to remedies, morning yoga routine, and healing tea recipes all in one place."
                        <div className="mt-4 font-bold not-italic text-sage-900">— Anita K., Healer Plan member</div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto mb-16">
                    <h2 className="text-3xl font-bold text-sage-900 mb-8 text-center">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <FAQItem question="Can I try Healer Plan before committing?" answer="Yes! We offer a 7-day free trial so you can experience the full power of personalized healing before subscribing." />
                        <FAQItem question="What happens after my 3 daily consultations on the Free Plan?" answer="You'll need to wait 24 hours for your consultation limit to reset, or upgrade to Healer Plan for unlimited access." />
                        <FAQItem question="Can I upgrade from Free to Healer anytime?" answer="Absolutely! Upgrade whenever you're ready. Your Wellness Profile and consultation history carry over seamlessly." />
                        <FAQItem question="Do I need the Healer Plan to create a Wellness Profile?" answer="No, the Wellness Profile (Wellness Passport) is available to all users, free and premium. It's the foundation for personalized guidance across all plans." />
                        <FAQItem question="What if I cancel my Healer Plan?" answer="You'll revert to the Free Plan but keep your Wellness Profile. Your Saved Library items will remain but won't be accessible until you resubscribe." />
                        <FAQItem question="What payment methods do you accept?" answer="We accept all major credit cards, debit cards, and secure online payments." />
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="bg-sage-900 rounded-3xl p-8 md:p-12 text-center text-white mb-16 relative overflow-hidden">
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">Start Your Healing Journey Today</h2>

                        <div className="flex flex-col md:flex-row justify-center gap-6 max-w-4xl mx-auto mt-10">
                            {/* Free Card Mini */}
                            <div className="bg-sage-800/50 backdrop-blur-sm border border-sage-700 rounded-2xl p-6 flex-1 text-left">
                                <div className="text-xl font-bold mb-2">Free Plan</div>
                                <div className="text-sage-300 text-sm mb-4">Explore & Learn</div>
                                <ul className="text-sm text-sage-200 space-y-2 mb-6">
                                    <li className="flex gap-2"><Check size={16} /> 3 daily consultations</li>
                                    <li className="flex gap-2"><Check size={16} /> Wellness Profile</li>
                                    <li className="flex gap-2"><Check size={16} /> Voice input</li>
                                    <li className="flex gap-2"><Check size={16} /> Basic botanical guidance</li>
                                </ul>
                                <div className="text-2xl font-bold mb-4">$0<span className="text-sm font-normal text-sage-400">/month</span></div>
                                <Link to="/app" className="block w-full py-2.5 px-4 bg-sage-700 hover:bg-sage-600 text-white text-center font-bold rounded-lg transition-colors">
                                    Get Started Free
                                </Link>
                            </div>

                            {/* Healer Card Mini */}
                            <div className="bg-white text-sage-900 rounded-2xl p-6 flex-1 text-left relative transform md:scale-105 shadow-xl">
                                <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-lg">POPULAR</div>
                                <div className="text-xl font-bold mb-2 flex items-center gap-2">Healer Plan <Sparkles size={16} className="text-yellow-500" /></div>
                                <div className="text-sage-600 text-sm mb-4">Transform & Heal</div>
                                <ul className="text-sm text-sage-700 space-y-2 mb-6">
                                    <li className="flex gap-2 font-medium"><Check size={16} className="text-green-600" /> Everything in Free, PLUS:</li>
                                    <li className="flex gap-2"><Check size={16} className="text-green-600" /> Unlimited consultations</li>
                                    <li className="flex gap-2"><Check size={16} className="text-green-600" /> Complete Rx with dosages</li>
                                    <li className="flex gap-2"><Check size={16} className="text-green-600" /> Yoga Aid + Nutri Heal</li>
                                    <li className="flex gap-2"><Check size={16} className="text-green-600" /> Saved Library</li>
                                </ul>
                                <div className="text-2xl font-bold mb-4">$14.99<span className="text-sm font-normal text-sage-600">/month</span></div>
                                <Link to="/app" className="block w-full py-2.5 px-4 bg-sage-600 hover:bg-sage-700 text-white text-center font-bold rounded-lg transition-colors">
                                    Start 14-Day Free Trial
                                </Link>
                                <div className="text-center text-xs text-sage-500 mt-2">Cancel anytime</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recommendation Helper */}
                <div className="bg-sage-100 rounded-2xl p-8 mb-12 text-center">
                    <h3 className="text-2xl font-bold text-sage-900 mb-6">Not Sure Which Plan Is Right for You?</h3>
                    <div className="grid md:grid-cols-2 gap-8 text-left max-w-4xl mx-auto">
                        <div>
                            <h4 className="font-bold text-sage-900 mb-3 text-lg">Start with Free if you want to:</h4>
                            <ul className="space-y-2 text-sage-700">
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Explore natural wellness without commitment</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Learn about your body constitution</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Try occasional consultations</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Understand Ayurvedic principles</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold text-sage-900 mb-3 text-lg">Choose Healer if you want to:</h4>
                            <ul className="space-y-2 text-sage-700">
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Actively address specific health concerns</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Receive complete, actionable prescriptions</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Build a comprehensive wellness practice</li>
                                <li className="flex gap-2"><div className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2"></div> Have unlimited access to expert guidance</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="text-center text-sage-600 pb-8 text-sm">
                    Questions? <a href="mailto:support@naturenani.com" className="font-bold text-sage-800 hover:underline">Contact Support</a> | Learn More <Link to="/about" className="font-bold text-sage-800 hover:underline">About NatureNani</Link> | <Link to="/faq" className="font-bold text-sage-800 hover:underline">FAQ</Link>
                </div>

            </div>
        </div>
    );
};

// Helper component for FAQs
const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    return (
        <div className="border border-sage-200 rounded-xl overflow-hidden bg-white">
            <button
                className="w-full flex items-center justify-between p-4 text-left font-bold text-sage-900 hover:bg-sage-50 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
            >
                {question}
                {isOpen ? <X size={20} className="text-sage-500" /> : <ArrowRight size={20} className="text-sage-500 rotate-90" />}
            </button>
            {isOpen && (
                <div className="p-4 pt-0 text-sage-700 bg-white border-t border-sage-100/50">
                    {answer}
                </div>
            )}
        </div>
    );
};

// Helper Icon for sections
const Target = ({ size = 24, className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10"></circle>
        <circle cx="12" cy="12" r="6"></circle>
        <circle cx="12" cy="12" r="2"></circle>
    </svg>
);
